/**
 * SHOWICT Quantum — renderer.
 *
 * Solo pinta: la detección vive en el motor legacy (vía adapter.js), la
 * derivación de niveles en adapter.js y las estadísticas en stats.js. Todo
 * el dibujo pasa por createChartDrawingApi (addSeries / addCustomSeries /
 * addLabel / addMarker) — nunca LWC directo ni DOM propio sobre el chart
 * (regla C.2), y ninguna serie toca la escala de precios
 * (autoscaleInfoProvider: () => null).
 *
 * Etiquetas de nivel (patrón bollinger_bands_quantum, fix P0-2): en LWC v4
 * una price line con `title` pinta DOS etiquetas (título en-pane + precio
 * crudo en el eje) y una LineSeries con title pinta UNA (el título en-pane,
 * aunque lastValueVisible sea false). Por eso TODOS los niveles son
 * LineSeries con el texto completo en `title` y este renderer no hace NI UNA
 * llamada a api.addPriceLine: exactamente una etiqueta por nivel.
 *
 * Geometría = f(datos + motor) EXCLUSIVAMENTE: prohibido visibleRange /
 * paneWidth; los items sin x real se omiten y el clip nunca se clampea.
 *
 * INVARIANTE de escala (v1.2.2): TODO time emitido a api.* ⊆ barTimes del
 * dataset de velas vigente en ESE render, sin excepciones. La escala temporal
 * es COMPARTIDA por todas las series del chart: un setData con un time fuera
 * del dataset hace que LWC inserte un punto fantasma en la escala (root
 * cause v1.2.1), desplaza los índices y puede reventar la propia serie de
 * velas en paint ("Value is null" en Candlestick). En la carrera de cambio
 * de timeframe (result del detect viejo contra velas del TF nuevo — o aún
 * sin velas) esta puerta es la que mantiene al indicador autocontenido:
 *   - barTimes vacío (transición, las velas del TF nuevo no han llegado) →
 *     no se pinta NADA (ni líneas, ni zonas, ni markers, ni labels);
 *   - markers y labels solo se emiten si su time es una vela real del
 *     dataset vigente (neutro en comportamiento: un marker cuyo time no es
 *     una vela no se dibuja de todos modos — los markers no tocan la escala,
 *     `_internal_setMarkers` ~línea 4707 del build dev de LWC);
 *   - líneas y zonas ya anclan a barTimes (v1.2.1).
 */

import { LineStyle } from 'lightweight-charts'
import { ZoneFillSeries } from './ZoneFillSeries.js'
import { toUtcSeconds } from '@/utils/utcTime'
import {
  QUANTUM_COLORS,
  ZONE_OPACITY,
  CLOSED_SIGNAL_ALPHA,
  LABEL_FONT,
  resolveConfig,
  resolveColor,
  resolveColors,
  withAlpha,
} from './constants.js'

// Límite de historial pintado (patrón PRQ, que recorta a 40).
const MAX_SIGNALS_DRAWN = 40

const COMMON_LINE_OPTS = {
  lastValueVisible: false,
  priceLineVisible: false,
  crosshairMarkerVisible: false,
  autoscaleInfoProvider: () => null,
}

/** Decimales razonables según magnitud del precio (mismo criterio que bollinger). */
function formatPrice(v) {
  if (!Number.isFinite(v)) return '—'
  if (Math.abs(v) >= 1000) return v.toFixed(1)
  if (Math.abs(v) >= 1) return v.toFixed(2)
  return v.toFixed(4)
}

/** R-múltiplo con un decimal ('1.0R'), tolerante a no finitos. */
function formatRMult(v) {
  return Number.isFinite(v) ? `${v.toFixed(1)}R` : '—'
}

// ── Guardas de pintado (defensa en profundidad, hotfix P0 2026-08-03) ───────
// lightweight-charts NO valida síncronamente: un null/NaN pasa silencioso por
// setData y revienta DIFERIDO en el siguiente frame de paint ("Uncaught
// Error: Value is null"). El core (createChartDrawingApi) envuelve
// addSeries.setData, pero NO envuelve addCustomSeries.setData: esa es la única
// vía sin filtro, así que TODA la validación de custom series es responsabilidad
// nuestra. Regla: ningún dato inválido (time null/''/NaN, valor no finito)
// cruza una llamada a api.* desde este renderer; nivel ausente = no se pinta.
//
// Frontera de tiempos: TODO time que cruza a api.* se normaliza a segundos UTC
// con el conversor COMPARTIDO @/utils/utcTime (v1.3.2 — acepta segundos,
// milisegundos, strings 'YYYY-MM-DD' de los TFs diarios+, ISO y business-day
// objects de LWC). Si no normaliza, el item se omite (nunca se pinta con un
// time dudoso).

function isValidPrice(v) {
  return Number.isFinite(v)
}

// ── Anclaje de extremos a velas reales (fix v1.2.1) ─────────────────────────
// Las líneas de nivel comparten el universo temporal de las zonas: SOLO
// tiempos que existen en barTimes. Root cause confirmado en el source de LWC
// v4 (dist/lightweight-charts.development.mjs): `_internal_setSeriesData`
// (~línea 11910) crea un NUEVO punto en la escala temporal para cualquier
// time de serie que no exista ya (`createEmptyTimePointData`), así que una
// línea con time más allá de la última vela EXTIENDE la escala a la derecha
// del último dato — el "simula señal viva" reportado en BTC 4H cuando
// closeTime no existía en la serie de velas (hueco/whitespace/recarga).
// Regla: sin vela real para un extremo, la línea NO se pinta (items sin x
// real se omiten, nunca se reclampa a un borde).

/** Primera vela con time ≥ t (times ascendente); null si no hay. */
function firstBarTimeAtOrAfter(times, t) {
  for (const x of times) {
    if (x >= t) return x
  }
  return null
}

/** Última vela con time ≤ t (times ascendente); null si no hay. */
function lastBarTimeAtOrBefore(times, t) {
  for (let i = times.length - 1; i >= 0; i--) {
    if (times[i] <= t) return times[i]
  }
  return null
}

/** Punto {time, value} válido para LineSeries (time ya normalizado). */
function isValidLinePoint(p) {
  return p != null && toUtcSeconds(p.time) != null && isValidPrice(p.value)
}

/** Punto {time, upper, lower} válido para ZoneFillSeries (time ya normalizado). */
function isValidZonePoint(p) {
  return p != null && toUtcSeconds(p.time) != null && isValidPrice(p.upper) && isValidPrice(p.lower)
}

function addLine(api, title, color, lineWidth, lineStyle, points) {
  // Re-valida y normaliza en el punto de llamada: una serie con un solo punto
  // inválido no se crea (LWC rechazaría toda la serie en el wrapper del core).
  const clean = (Array.isArray(points) ? points.filter(isValidLinePoint) : [])
    .map(p => ({ time: toUtcSeconds(p.time), value: p.value }))
  if (clean.length === 0) return
  // El `title` es el TEXTO COMPLETO de la etiqueta (fix P0-2, patrón BBQ):
  // es la única etiqueta que pinta la línea (en-pane, pegada al eje).
  const s = api.addSeries(c => c.addLineSeries({
    title,
    color,
    lineWidth,
    lineStyle,
    ...COMMON_LINE_OPTS,
  }))
  if (s) s.setData(clean)
}

/** Custom series de relleno con TODOS sus puntos validados antes de setData. */
function addZoneSeries(api, points, fillColor, chart) {
  const clean = (Array.isArray(points) ? points.filter(isValidZonePoint) : [])
    .map(p => ({ time: toUtcSeconds(p.time), upper: p.upper, lower: p.lower, color: p.color }))
    // LWC rechaza (throw) series con tiempos desordenados o duplicados; un
    // setData fallido dejaría la serie VACÍA y visible → "Value is null" en el
    // siguiente paint/crosshair. Orden + dedup + retirada en caso de fallo.
    .sort((a, b) => (a.time < b.time ? -1 : a.time > b.time ? 1 : 0))
    .filter((p, i, arr) => i === 0 || p.time !== arr[i - 1].time)
  if (clean.length < 2) return
  const s = api.addCustomSeries(new ZoneFillSeries(), {
    color: 'rgba(0,0,0,0)',
    fillColor,
    lineWidth: 0,
    ...COMMON_LINE_OPTS,
  })
  if (!s) return
  try {
    s.setData(clean.map(p => ({ time: p.time, upper: p.upper, lower: p.lower, color: p.color ?? fillColor })))
  } catch {
    // Nunca dejar una serie custom vacía visible en el chart.
    try { chart?.removeSeries?.(s) } catch { /* noop */ }
  }
}

export function renderShowict(chart, candleSeries, result, config, api) {
  if (!result || !api) return
  const cfg = resolveConfig(config)
  if (cfg.enabled === false || result.gated) return

  const signals = Array.isArray(result.signals) ? result.signals : []
  const fakes = Array.isArray(result.fakes) ? result.fakes : []
  const pivotLabels = Array.isArray(result.pivotLabels) ? result.pivotLabels : []
  const activeLevels = result.activeLevels && typeof result.activeLevels === 'object' ? result.activeLevels : null
  if (signals.length === 0 && fakes.length === 0 && pivotLabels.length === 0 && !activeLevels) return

  const colors = resolveColors(cfg)
  const zoneColor = resolveColor(cfg.zoneColor, null)

  // Velas visibles: para el extremo de niveles H/L, de la señal activa y el
  // barrido de zonas. Tiempos ya normalizados (toUtcSeconds).
  const bars = candleSeries && typeof candleSeries.data === 'function' ? candleSeries.data() : []
  const barTimes = (Array.isArray(bars) ? bars : [])
    .map(b => toUtcSeconds(b?.time))
    .filter(t => t != null)

  // Puerta de coherencia dataset (v1.2.2): sin velas en el dataset estamos en
  // la ventana de transición de TF (la serie se reseteó y las velas nuevas
  // aún no han llegado) y el `result` es del detect del TF anterior. Pintar
  // cualquier cosa con tiempos del TF viejo contra una escala vacía/nueva es
  // el detonante del crash de velas — no se pinta NADA hasta que lleguen.
  if (barTimes.length === 0) return

  // Universo temporal vigente: todo time emitido a api.* debe pertenecer a él
  // (invariante del header). Set para el filtrado de markers y labels.
  const barTimesSet = new Set(barTimes)

  const firstTime = barTimes.length > 0 ? barTimes[0] : null
  const lastTime = barTimes.length > 0 ? barTimes[barTimes.length - 1] : null

  // ── Niveles activos H/L del motor: LineSeries de 2 puntos (primera→última
  // vela), dashed 1px, con el título completo en `title` — misma estética que
  // las price lines del legacy pero con UNA sola etiqueta (fix P0-2). ───────
  if (cfg.showLevels && activeLevels && firstTime != null && lastTime != null) {
    if (Number.isFinite(activeLevels.activeH)) {
      addLine(api, 'H activo', QUANTUM_COLORS.bull, 1, LineStyle.Dashed, [
        { time: firstTime, value: activeLevels.activeH },
        ...(lastTime !== firstTime ? [{ time: lastTime, value: activeLevels.activeH }] : []),
      ])
    }
    if (Number.isFinite(activeLevels.activeL)) {
      addLine(api, 'L activo', QUANTUM_COLORS.bear, 1, LineStyle.Dashed, [
        { time: firstTime, value: activeLevels.activeL },
        ...(lastTime !== firstTime ? [{ time: lastTime, value: activeLevels.activeL }] : []),
      ])
    }
  }

  // ── Etiquetas HH / LH / HL / LL en los swings (markers, convención legacy) ─
  if (cfg.showHHHL) {
    for (const p of pivotLabels) {
      const t = toUtcSeconds(p?.time)
      if (t == null || !Number.isFinite(p?.price) || !barTimesSet.has(t)) continue
      api.addMarker({
        time: t,
        position: p.isHigh ? 'aboveBar' : 'belowBar',
        color: p.isHigh ? QUANTUM_COLORS.bull : QUANTUM_COLORS.bear,
        shape: 'circle',
        text: p.label,
        size: 0,
      })
    }
  }

  // ── Fakes 'F' (solo pintura, nunca budget) ────────────────────────────────
  if (cfg.showFakes) {
    for (const f of fakes) {
      const t = toUtcSeconds(f?.time)
      if (t == null || !barTimesSet.has(t)) continue
      api.addMarker({
        time: t,
        position: f.type === 'fakeU' ? 'aboveBar' : 'belowBar',
        color: colors.fake,
        shape: 'circle',
        text: 'F',
        size: 1,
      })
    }
  }

  const signalsToDraw = signals.slice(-MAX_SIGNALS_DRAWN)

  // ── Etiquetas históricas con el texto ORIGINAL del motor ('A+'/'A-'/'A') ──
  if (cfg.showSignals) {
    for (const sig of signalsToDraw) {
      const t = toUtcSeconds(sig.time)
      if (t == null || !barTimesSet.has(t)) continue
      const isBull = sig.direction === 'LONG'
      const color = isBull ? QUANTUM_COLORS.bull : QUANTUM_COLORS.bear
      api.addLabel({
        time: t,
        price: Number.isFinite(sig.entry) ? sig.entry : sig.levelPrice,
        text: sig.label,
        color,
        bgColor: withAlpha(color, 0.12),
        boxStyle: 'flag',
        isBull,
        font: LABEL_FONT,
      })
    }
  }

  if (cfg.showOutcomeMarkers) {
    for (const sig of signalsToDraw) {
      const isLong = sig.direction === 'LONG'
      // Marcadores de TP alcanzado: un marker verde en la vela de cada toque
      // (tp1Hit/tp2Hit/tp3Hit son timestamps). Si llegó a TP3 se ven los 3.
      for (const [hit, name] of [[sig.tp1Hit, 'TP1'], [sig.tp2Hit, 'TP2'], [sig.tp3Hit, 'TP3']]) {
        const t = toUtcSeconds(hit)
        if (t == null || !barTimesSet.has(t)) continue
        api.addMarker({
          time: t,
          position: isLong ? 'aboveBar' : 'belowBar',
          color: QUANTUM_COLORS.bull,
          shape: 'circle',
          text: name,
          size: 0,
        })
      }
      // Marcadores de outcome final en closeTime: ✓ en win (TP3) y be (SL
      // tras TP1), ✗ en loss, 'Inv' gris neutro en invalidada (señal
      // contraria, distinto del ✗ de SL y de los markers verdes de TP).
      const closeT = toUtcSeconds(sig.closeTime)
      if (closeT == null || !barTimesSet.has(closeT)) continue
      if (sig.outcome === 'win' || sig.outcome === 'be') {
        api.addMarker({
          time: closeT,
          position: isLong ? 'aboveBar' : 'belowBar',
          color: QUANTUM_COLORS.bull,
          shape: 'circle',
          text: '✓',
          size: 1,
        })
      } else if (sig.outcome === 'loss') {
        api.addMarker({
          time: closeT,
          position: isLong ? 'belowBar' : 'aboveBar',
          color: QUANTUM_COLORS.bear,
          shape: 'circle',
          text: '✗',
          size: 1,
        })
      } else if (sig.outcome === 'invalidada') {
        api.addMarker({
          time: closeT,
          position: isLong ? 'belowBar' : 'aboveBar',
          color: QUANTUM_COLORS.neutral,
          shape: 'circle',
          text: 'Inv',
          size: 0,
        })
      }
    }
  }

  // ── Niveles + zonas + etiqueta: señal activa o última (patrón PRQ:
  // levelsSource = activeSignal || lastSignal). Si la señal está CERRADA
  // (win/be/loss con closeTime), líneas y zonas terminan en la vela de
  // closeTime; solo las ABIERTAS extienden hasta la última vela (fix P1-4).
  // Se mantienen congelados tras el outcome final; no se borran. ─────────────
  const levelsSignal = result.activeSignal || result.lastSignal || null
  if (levelsSignal && Number.isFinite(levelsSignal.entry)) {
    const sigTime = toUtcSeconds(levelsSignal.time)
    if (sigTime != null) {
      const { entry, sl, tp1, tp2, tp3, direction, grade } = levelsSignal
      const isBull = direction === 'LONG'
      const closedTime = levelsSignal.outcome !== 'active' ? toUtcSeconds(levelsSignal.closeTime) : null
      const endTime = closedTime != null
        ? (closedTime > sigTime ? closedTime : sigTime)
        : (lastTime != null && lastTime > sigTime ? lastTime : sigTime)
      // Atenuación de señal CERRADA (v1.3.0): zonas, líneas de nivel y la
      // etiqueta-recuadro van con alpha reducido; los marcadores de outcome
      // (✓/✗/sticks TP/Inv) NO se atenúan — son la información. Con
      // fadeClosedSignals === false todo va a intensidad plena. Sin tocar
      // geometría ni anclajes: solo los colores.
      const fade = levelsSignal.outcome !== 'active' && cfg.fadeClosedSignals !== false
      const dim = (color) => (fade ? withAlpha(color, CLOSED_SIGNAL_ALPHA) : color)
      const zoneAlpha = fade ? ZONE_OPACITY * CLOSED_SIGNAL_ALPHA : ZONE_OPACITY

      if (cfg.showZones && barTimes.length > 0) {
        // Zona de riesgo (entry↔SL, bear) y de beneficio (entry↔TP3 o último
        // TP disponible, bull), alpha fijo ZONE_OPACITY. Se barren las velas
        // desde la señal hasta endTime (closeTime si está cerrada).
        const rewardTarget = Number.isFinite(tp3) ? tp3 : Number.isFinite(tp2) ? tp2 : tp1
        const span = barTimes.filter(t => t >= sigTime && t <= endTime)
        if (span.length >= 2 && Number.isFinite(sl) && Number.isFinite(rewardTarget)) {
          const riskColor = withAlpha(zoneColor ?? QUANTUM_COLORS.bear, zoneAlpha)
          const rewardColor = withAlpha(zoneColor ?? QUANTUM_COLORS.bull, zoneAlpha)
          const zones = [
            { upper: Math.max(entry, sl), lower: Math.min(entry, sl), color: riskColor },
            { upper: Math.max(entry, rewardTarget), lower: Math.min(entry, rewardTarget), color: rewardColor },
          ]
          for (const z of zones) {
            if (!(z.upper > z.lower)) continue
            addZoneSeries(api, span.map(t => ({ time: t, upper: z.upper, lower: z.lower, color: z.color })), z.color, chart)
          }
        }
      }

      if (cfg.showLevels) {
        // Desde la vela de señal hasta endTime (closeTime en cerradas, última
        // vela en abiertas), con los extremos ANCLADOS a velas reales
        // (fix v1.2.1: mismo universo temporal que las zonas — un time que no
        // existe en la escala extendería la línea más allá del último dato).
        // Sin vela real para un extremo, la línea no se pinta.
        const lineStartTime = firstBarTimeAtOrAfter(barTimes, sigTime)
        const lineEndTime = lastBarTimeAtOrBefore(barTimes, endTime)
        // Jerarquía visual PRQ: entry 1px neutra, SL 1.5 bear sólida, TP1 1.5
        // bull sólida, TP2 1.5 bull punteada, TP3 punteada larga (con TP2
        // sólida, TP3 se degrada a Dashed).
        // El `title` lleva el TEXTO COMPLETO de la etiqueta (fix P0-2) SOLO en
        // señal ABIERTA. En señal CERRADA (cualquier outcome) las líneas
        // pierden el rótulo del eje (v1.2.3): el rótulo en-pane de LWC depende
        // de title !== '' (SeriesPriceAxisView), no de lastValueVisible — con
        // title vacío el segmento histórico queda en el panel sin etiqueta en
        // el eje, que ya no simula señal viva.
        const axisTitle = (text) => (levelsSignal.outcome !== 'active' ? '' : text)
        const levelLine = (title, price, color, width, style) => {
          if (!Number.isFinite(price)) return
          if (lineStartTime == null || lineEndTime == null || lineEndTime < lineStartTime) return
          addLine(api, title, color, width, style, [
            { time: lineStartTime, value: price },
            ...(lineEndTime !== lineStartTime ? [{ time: lineEndTime, value: price }] : []),
          ])
        }
        const gradeLabel = typeof grade === 'string' && grade ? grade : ''
        levelLine(axisTitle(`Entrada ${gradeLabel} ${formatPrice(entry)}`.replace(/\s+/g, ' ').trim()), entry, dim(colors.entry), 1, LineStyle.Solid)
        levelLine(axisTitle(`SL ${formatPrice(sl)}`), sl, dim(colors.sl), 1.5, LineStyle.Solid)
        levelLine(axisTitle(`TP1 (${formatRMult(cfg.tpMults.tp1)}) ${formatPrice(tp1)}`), tp1, dim(colors.tp), 1.5, LineStyle.Solid)
        levelLine(axisTitle(`TP2 (${formatRMult(cfg.tpMults.tp2)}) ${formatPrice(tp2)}`), tp2, dim(colors.tp), 1.5, cfg.tp2LineStyle === 'dashed' ? LineStyle.Dashed : LineStyle.Solid)
        levelLine(axisTitle(`TP3 (${formatRMult(cfg.tpMults.tp3)}) ${formatPrice(tp3)}`), tp3, dim(colors.tp), 1.5, cfg.tp2LineStyle === 'dashed' ? LineStyle.LargeDashed : LineStyle.Dashed)

        // Etiqueta de señal activa con el GRADO (boxStyle flag, molde PRQ).
        // Solo si la vela de la señal existe en el dataset vigente
        // (invariante v1.2.2: todo time emitido ⊆ barTimes).
        const dirColor = isBull ? QUANTUM_COLORS.bull : QUANTUM_COLORS.bear
        if (barTimesSet.has(sigTime)) api.addLabel({
          time: sigTime,
          price: entry,
          text: `${direction} ${gradeLabel} ${formatPrice(entry)}`.replace(/\s+/g, ' ').trim(),
          color: dim(dirColor),
          bgColor: withAlpha(dirColor, fade ? 0.12 * CLOSED_SIGNAL_ALPHA : 0.12),
          boxStyle: 'flag',
          isBull,
          font: LABEL_FONT,
        })
      }
    }
  }
}
